import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Shield, Users, Key, Plus, Edit, Trash2, RefreshCw, Lock, Search, Eye, Copy, Star, LayoutGrid, List, Filter } from 'lucide-react';

import { useGestionRoles } from './useGestionRoles';
import GestionRolesModals from './GestionRolesModals';
import { RoleUsersDialog } from './RoleUsersDialog';

export default function GestionRoles(props) {
  const ctx = useGestionRoles(props);
  const [usersRole, setUsersRole] = useState(null);
  const { _deselectAllPermissions, _expandedCategories, filterType, filteredRoles, getRoleConfig, _groupedPermissions, _handleCreateRole, handleDeleteRole, handleDuplicateRole, _handleUpdateRole, isSuperAdmin, loadData, loading, openCreateModal, openEditModal, openPermissionsView, _permissions, _roleForm, searchTerm, _selectAllInCategory, _selectAllPermissions, _selectedRole, _showPermissionsModal, _showRoleModal, stats, _toggleCategory, _togglePermission, viewMode, setViewMode, setFilterType, setSearchTerm } = ctx;
  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="h-7 w-7 text-purple-600" />
            Gestion des Rôles et Permissions
          </h2>
          <p className="text-gray-500 mt-1">
            Configurez les rôles et attribuez des permissions aux utilisateurs
          </p>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          {/* Recherche */}
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Rechercher un rôle..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 w-56"
            />
          </div>
          
          {/* Filtre */}
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-40">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les rôles</SelectItem>
              <SelectItem value="system">Rôles système</SelectItem>
              <SelectItem value="custom">Rôles personnalisés</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Vue */}
          <div className="flex border rounded-lg overflow-hidden">
            <Button 
              variant={viewMode === 'grid' ? 'secondary' : 'ghost'} 
              size="sm"
              onClick={() => setViewMode('grid')}
              className="rounded-none"
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button 
              variant={viewMode === 'list' ? 'secondary' : 'ghost'} 
              size="sm"
              onClick={() => setViewMode('list')}
              className="rounded-none"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Rafraîchir */}
          <Button variant="outline" onClick={loadData} size="sm">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          
          {/* Nouveau rôle */}
          {isSuperAdmin && (
            <Button onClick={openCreateModal} className="bg-purple-600 hover:bg-purple-700">
              <Plus className="h-4 w-4 mr-1" />
              Nouveau rôle
            </Button>
          )}
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-purple-700">{stats.total}</div>
                <div className="text-sm text-purple-600">Rôles totaux</div>
              </div>
              <Shield className="h-10 w-10 text-purple-300" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-orange-50 to-white border-orange-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-orange-700">{stats.system}</div>
                <div className="text-sm text-orange-600">Rôles système</div>
              </div>
              <Lock className="h-10 w-10 text-orange-300" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-blue-700">{stats.custom}</div>
                <div className="text-sm text-blue-600">Personnalisés</div>
              </div>
              <Star className="h-10 w-10 text-blue-300" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-white border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-green-700">{stats.totalPermissions}</div>
                <div className="text-sm text-green-600">Permissions</div>
              </div>
              <Key className="h-10 w-10 text-green-300" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-sky-50 to-white border-sky-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-sky-700">{stats.totalUsers}</div>
                <div className="text-sm text-sky-600">Utilisateurs</div>
              </div>
              <Users className="h-10 w-10 text-sky-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Liste des rôles */}
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="h-10 w-10 animate-spin text-purple-600" />
        </div>
      ) : viewMode === 'grid' ? (
        /* Vue Grille */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredRoles.map((role) => {
            const config = getRoleConfig(role);
            const RoleIcon = config.icon;
            
            return (
              <Card 
                key={role.name} 
                className="hover:shadow-lg transition-all duration-200 overflow-hidden"
              >
                {/* Barre de couleur */}
                <div className="h-2" style={{ backgroundColor: config.color }} />
                
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div 
                        className={`p-2 rounded-lg ${config.bgColor}`}
                        style={{ color: config.color }}
                      >
                        <RoleIcon className="h-5 w-5" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{role.display_name}</CardTitle>
                        <CardDescription className="text-xs font-mono">
                          {role.name}
                        </CardDescription>
                      </div>
                    </div>
                    
                    {role.is_system && (
                      <Badge className="bg-slate-800 text-white font-medium">
                        <Lock className="h-3 w-3 mr-1" />
                        Système
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  {role.description && (
                    <p className="text-sm text-gray-600 line-clamp-2">{role.description}</p>
                  )}
                  
                  {/* Stats du rôle */}
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-1 text-gray-600">
                      <Users className="h-4 w-4" />
                      <span className="font-medium">{role.user_count || 0}</span>
                      <span className="text-gray-400">utilisateur(s)</span>
                    </div>
                    <div className="flex items-center gap-1 text-gray-600">
                      <Key className="h-4 w-4" />
                      <span className="font-medium">{role.permissions?.length || 0}</span>
                      <span className="text-gray-400">permission(s)</span>
                    </div>
                  </div>
                  
                  {/* Aperçu des permissions */}
                  <div className="flex flex-wrap gap-1">
                    {(role.permissions || []).slice(0, 3).map((perm) => (
                      <Badge variant="outline" className="text-xs border-gray-500 text-gray-700 font-medium">
                        {perm.split('.')[1] || perm}
                      </Badge>
                    ))}
                    {(role.permissions?.length || 0) > 3 && (
                      <Badge className="bg-gray-800 text-white text-xs font-medium">
                        +{role.permissions.length - 3}
                      </Badge>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2 pt-2 border-t">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => openPermissionsView(role)}
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      Voir
                    </Button>
                    
                    {isSuperAdmin && (
                      <>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openEditModal(role)}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleDuplicateRole(role)}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                        
                        {!role.is_system && (
                          <Button size="sm" variant="outline" onClick={() => setUsersRole(role)} data-testid={`role-users-btn-${role.name}`} title="Personnes ayant ce rôle">
                            <Users className="h-3 w-3" />
                          </Button>
                        )}

                        {!role.is_system && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteRole(role.name)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        /* Vue Liste/Tableau */
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Rôle</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Type</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Utilisateurs</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Permissions</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Description</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredRoles.map((role) => {
                    const config = getRoleConfig(role);
                    const RoleIcon = config.icon;
                    
                    return (
                      <tr key={role.name} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-1 h-10 rounded-full"
                              style={{ backgroundColor: config.color }}
                            />
                            <div 
                              className={`p-2 rounded-lg ${config.bgColor}`}
                              style={{ color: config.color }}
                            >
                              <RoleIcon className="h-4 w-4" />
                            </div>
                            <div>
                              <div className="font-medium">{role.display_name}</div>
                              <div className="text-xs text-gray-500 font-mono">{role.name}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {role.is_system ? (
                            <Badge className="bg-slate-800 text-white font-medium">
                              <Lock className="h-3 w-3 mr-1" />
                              Système
                            </Badge>
                          ) : (
                            <Badge className="bg-blue-800 text-white font-medium">
                              <Star className="h-3 w-3 mr-1" />
                              Personnalisé
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge variant="outline" className="font-mono bg-slate-700 text-white border-slate-800 font-semibold">
                            {role.user_count || 0}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge className="bg-green-800 text-white font-mono font-medium">
                            {role.permissions?.length || 0}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-600 line-clamp-1">
                            {role.description || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-1">
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => openPermissionsView(role)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            
                            {isSuperAdmin && (
                              <>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => openEditModal(role)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => handleDuplicateRole(role)}
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                                
                                {!role.is_system && (
                                  <Button size="sm" variant="ghost" onClick={() => setUsersRole(role)} data-testid={`role-users-list-btn-${role.name}`} title="Personnes ayant ce rôle">
                                    <Users className="h-4 w-4" />
                                  </Button>
                                )}

                                {!role.is_system && (
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    className="text-red-600"
                                    onClick={() => handleDeleteRole(role.name)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <GestionRolesModals ctx={ctx} />
      {usersRole && <RoleUsersDialog role={usersRole} onClose={() => setUsersRole(null)} onChanged={loadData} />}
    </div>
  );
}
